import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { type FormikHelpers } from 'formik'
import * as React from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import { useAdminAccess, useApi, type APIResponse } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import Warning from 'src/imgs/classroom_icon.svg'
import { Form } from 'src/views/components/forms/formik/Form'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import { CourseEditFormContent } from './CourseEditFormContent'
import { CourseSyllabusFormSection } from './CourseSyllabusFormSection'
import { courseAddFormValuesToPutAdministratorCourseBody, courseSyllabusToFormValues } from './mapModels'
import { type CourseAddFormValues } from './types'
import { useCourseEditPageData } from './useCourseEditPageData'

export default function AdministratorCourseEditPage(): JSX.Element {
  const headers = useAuthenticatedHeaders()
  const locale = useLocale()
  const t = useTranslatable()
  const { courseId } = useParams<{ courseId: string }>()
  const [isExportLoading, setIsExportLoading] = React.useState(false)
  const [searchParams] = useSearchParams()
  const activeSemester = useActiveSemester()
  const semId = String(searchParams.get('semId') ?? activeSemester?.id ?? '').trim()
  const navigate = useNavigate()
  const { isMainAdministrator } = useAdminAccess()

  const {
    course,
    faculties,
    degrees,
    courseDefinitions,
    courseTypeOptions,
    creditTypes,
    initialValues,
    isLoading,
    mutateCourse,
  } = useCourseEditPageData({
    courseId,
    headers,
    semId,
    locale,
  })

  const {
    data: syllabus,
    isLoading: isSyllabusLoading,
    mutate: refetchSyllabus,
  }: APIResponse<Api.CourseSyllabus | null, Api.getAdministratorCourseSyllabusErr> = useApi({
    endpoint: Api.getAdministratorCourseSyllabus,
    params: React.useMemo(
      () => ({
        headers,
        args: { id: courseId! },
        query: {
          semId: semId,
        },
      }),
      [courseId, headers, semId]
    ),
    shouldFetch: courseId != null,
    suspense: false,
  })

  const mergedInitialValues = React.useMemo(
    (): CourseAddFormValues => ({
      ...initialValues,
      ...courseSyllabusToFormValues(syllabus ?? null),
    }),
    [initialValues, syllabus]
  )

  const onSubmitCourseAndSyllabus = React.useCallback(
    async (values: CourseAddFormValues, formikHelpers: FormikHelpers<CourseAddFormValues>) => {
      if (courseId == null || semId.length === 0) return
      const parsedSemId = Number.parseInt(semId, 10)
      if (!Number.isFinite(parsedSemId)) return

      try {
        await Api.putAdministratorCourse({
          headers,
          args: { id: courseId },
          query: { semId: parsedSemId },
          body: courseAddFormValuesToPutAdministratorCourseBody(values, parsedSemId),
        })

        toast.success(t('common:updated_successfully'))
        await mutateCourse()
        await refetchSyllabus()
        navigate(`/${locale}/administrator/administration/courses`)
        formikHelpers.resetForm()
      } catch (error) {
        toast.error(t('error:an_error_occurred'))
        throw error
      }
    },
    [courseId, headers, mutateCourse, refetchSyllabus, semId, t, navigate, locale]
  )

  const onDownloadSyllabus = React.useCallback(async () => {
    if (courseId == null || isExportLoading) return

    setIsExportLoading(true)
    try {
      const result = await Api.getAdministratorCourseSyllabusExport({
        headers,
        args: { id: courseId },
        query: { mode: 'download' },
      })

      const downloadUrl = result.downloadUrl
      if (downloadUrl == null) throw new Error('Missing download url')

      location.replace(downloadUrl)
    } catch {
      toast.error(t('error:an_error_occurred'))
    } finally {
      setIsExportLoading(false)
    }
  }, [courseId, headers, isExportLoading, t])

  if (isLoading || isSyllabusLoading) return <Loader className="m-auto flex" />
  if (course == null) return <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />

  return (
    <Form initialValues={mergedInitialValues} onSubmit={onSubmitCourseAndSyllabus} enableReinitialize>
      <div className="mb-3 flex justify-end">
        <button
          type="button"
          onClick={() => {
            void onDownloadSyllabus()
          }}
          className="text-primaryBlueLink no-underline disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="downloadSyllabusExport"
          disabled={isExportLoading}
        >
          <FontAwesomeIcon icon={ProRegularSvgIcons.faDownload} /> {t('course:download_syllabus')}
        </button>
      </div>
      <CourseEditFormContent
        headers={headers}
        course={course}
        faculties={faculties ?? []}
        degrees={degrees}
        courseDefinitions={courseDefinitions}
        courseTypeOptions={courseTypeOptions}
        creditTypes={creditTypes}
        isMainAdministrator={isMainAdministrator}
      />
      <CourseSyllabusFormSection showFormError={false} />
    </Form>
  )
}
